import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, BellOff, Check, Loader2, Smartphone } from "lucide-react";
import { Capacitor } from "@capacitor/core";

interface NativePushCardProps {
  isNativePlatform: boolean;
  nativePushAvailable: boolean;
  nativePushPermission: 'granted' | 'denied' | 'prompt';
  nativePushEnabled: boolean;
  isNativeSubscribing: boolean;
  onEnable: () => void;
  onDisable: () => void;
}

export function NativePushCard({
  isNativePlatform,
  nativePushAvailable,
  nativePushPermission,
  nativePushEnabled,
  isNativeSubscribing,
  onEnable,
  onDisable,
}: NativePushCardProps) {
  if (!isNativePlatform) return null;

  return (
    <Card className="card-elevated border-none shadow-premium">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Smartphone className="h-5 w-5" />
          Mobile Push Notifications
        </CardTitle>
        <CardDescription>
          Receive native push notifications on your {Capacitor.getPlatform() === 'ios' ? 'iPhone' : 'Android device'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!nativePushAvailable ? (
          <div className="flex items-start gap-3 p-4 bg-muted rounded-lg">
            <AlertCircle className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium">Not Available</p>
              <p className="text-sm text-muted-foreground">
                Push notifications are not available on this device.
              </p>
            </div>
          </div>
        ) : nativePushPermission === 'denied' ? (
          <div className="flex items-start gap-3 p-4 bg-destructive/10 rounded-lg">
            <BellOff className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-destructive">Permission Denied</p>
              <p className="text-sm text-muted-foreground">
                Push notifications are blocked. Please enable them in your device's Settings app under SunSquatcher.
              </p>
            </div>
          </div>
        ) : nativePushEnabled ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Check className="h-5 w-5 text-green-600" />
                <div>
                  <p className="font-medium">Push notifications enabled</p>
                  <p className="text-sm text-muted-foreground">
                    You'll receive alerts directly on your device
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={onDisable}
                disabled={isNativeSubscribing}
                aria-busy={isNativeSubscribing}
              >
                {isNativeSubscribing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Disabling...
                  </>
                ) : (
                  "Disable"
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Enable mobile notifications</p>
                <p className="text-sm text-muted-foreground">
                  Get alerts even when the app is closed
                </p>
              </div>
              <Button
                onClick={onEnable}
                disabled={isNativeSubscribing}
                aria-busy={isNativeSubscribing}
              >
                {isNativeSubscribing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Enabling...
                  </>
                ) : (
                  "Enable"
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
